import React from 'react';
import { Box, Text } from '@cowbell/ui';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { rem } from '../../../components/panels/panelLayout';
import { useDashboardPanelTokens } from './dashboardPanelTokens';

interface MissingDomainNoticeProps {
  readonly onAddDomain: () => void;
  /** Rendered by the caller so the notice does not pick its own button. */
  readonly action?: React.ReactNode;
}

/**
 * The account has no primary web domain, so no report can be generated.
 *
 * Framed as the one thing the reader can do about it rather than as a warning
 * about a failure: it leads with the action, says plainly what is lost without
 * it, and puts the way to fix it beside the sentence.
 */
export function MissingDomainNotice({
  action,
}: Readonly<MissingDomainNoticeProps>) {
  const tokens = useDashboardPanelTokens();

  return (
    <Box
      data-qa="RISK_REPORT_MISSING_DOMAIN"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: rem(12),
        flexWrap: 'wrap',
        padding: rem(14),
        borderRadius: rem(8),
        backgroundColor: tokens.cautionSurface,
      }}
    >
      {/* Icon and copy are one row: left to wrap independently the glyph took
          a line of its own at tile width, making the notice needlessly tall. */}
      <Box
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: rem(10),
          flex: '1 1 auto',
          minWidth: rem(180),
        }}
      >
        <FontAwesomeIcon
          icon={faTriangleExclamation}
          color={tokens.caution}
          style={{ fontSize: rem(16), flexShrink: 0, marginTop: rem(2) }}
        />

        <Box style={{ minWidth: 0 }}>
          <Text
            style={{
              fontSize: rem(13),
              fontWeight: 700,
              lineHeight: 1.35,
              color: tokens.ink,
            }}
          >
            Add your primary web domain to unlock the Risk Report
          </Text>
          <Text
            style={{
              marginTop: rem(2),
              fontSize: rem(12.5),
              lineHeight: 1.45,
              color: tokens.inkFaint,
            }}
          >
            Without it we can&apos;t scan your external surface or generate
            remediation guidance.
          </Text>
        </Box>
      </Box>

      {/* Pushed to the right edge rather than sitting against the copy. */}
      <Box style={{ marginLeft: 'auto', flexShrink: 0 }}>{action}</Box>
    </Box>
  );
}

export default MissingDomainNotice;
